// src/api/pagesApi.js

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:5000";

async function readJson(res) {
  const text = await res.text().catch(() => "");
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { __raw: text };
  }
}

// Public fetch (no Authorization header)
async function apiJsonPublic(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, { ...options });
  const data = await readJson(res);

  if (!res.ok) {
    const err = new Error(data?.error || data?.message || "Request failed");
    err.data = data;
    throw err;
  }
  return data;
}
const LANG_CACHE_KEY = "sb_lang_dict_cache_v1";

function readLangCache() {
  try {
    return JSON.parse(localStorage.getItem(LANG_CACHE_KEY) || "null");
  } catch {
    return null;
  }
}

function writeLangCache(cacheObj) {
  try {
    localStorage.setItem(LANG_CACHE_KEY, JSON.stringify(cacheObj));
  } catch { }
}

export async function preloadLanguageDict(lang, opts = {}) {
  const cache = readLangCache();
  if (cache?.[lang]) return cache[lang];

  const res = await fetch(`${API_BASE}/api/language/${lang}`, { signal: opts.signal });
  const data = await readJson(res);

  if (!res.ok) return null;

  const next = { ...(cache || {}) };
  next[lang] = data || {};
  writeLangCache(next);

  return data || {};
}
function withAuthHeaders(headers = {}) {
  const token = localStorage.getItem("sb_token");
  if (!token) return headers;
  return { ...headers, Authorization: `Bearer ${token}` };
}

async function apiJson(path, options = {}) {
  const headers = withAuthHeaders(options.headers || {});
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  const data = await readJson(res);

  if (!res.ok) {
    const err = new Error(data?.error || data?.message || "Request failed");
    err.data = data;
    err.status = res.status;
    throw err;
  }
  return data;
}

export const pagesApi = {
  // ---------- Auth ----------
  // ✅ supports slug for manager login binding
  login: async ({ email, password, slug }, opts = {}) => {
    const body = { email, password };
    if (slug) body.slug = slug;

    return apiJson("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: opts.signal,
    });
  },

  me: async (opts = {}) => {
    return apiJson("/api/auth/me", { signal: opts.signal });
  },

  // ---------- Pages ----------
  // Admin list (uses token)
  list: async (opts = {}) => {
    const data = await apiJson("/api/pages", { signal: opts.signal });
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.pages)) return data.pages;
    return [];
  },

  // Public list for homepage (no token)
  listPublic: async (opts = {}) => {
    const data = await apiJsonPublic("/api/pages-public", { signal: opts.signal });
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.pages)) return data.pages;
    return [];
  },

  getById: async (id, opts = {}) => {
    return apiJson(`/api/pages/${encodeURIComponent(id)}`, { signal: opts.signal });
  },

  // public page load (no token needed)
  getBySlug: async (slug, opts = {}) => {
    const s = String(slug || "").trim();
    return apiJsonPublic(`/api/pages/slug/${encodeURIComponent(s)}`, { signal: opts.signal });
  },

  create: async (payload, opts = {}) => {
    return apiJson("/api/pages", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload || {}),
      signal: opts.signal,
    });
  },

  update: async (id, payload, opts = {}) => {
    return apiJson(`/api/pages/${encodeURIComponent(id)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload || {}),
      signal: opts.signal,
    });
  },

  remove: async (id, opts = {}) => {
    return apiJson(`/api/pages/${encodeURIComponent(id)}`, {
      method: "DELETE",
      signal: opts.signal,
    });
  },

  // ---------- Section autosave ----------
  updateSection: async (pageId, sectionId, patch, opts = {}) => {
    return apiJson(
      `/api/pages/${encodeURIComponent(pageId)}/sections/${encodeURIComponent(sectionId)}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch || {}),
        signal: opts.signal,
      }
    );
  },

  // ---------- Nav autosave ----------
  updateNav: async (pageId, patch, opts = {}) => {
    return apiJson(`/api/pages/${encodeURIComponent(pageId)}/nav`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch || {}),
      signal: opts.signal,
    });
  },

  // ---------- Footer autosave ----------
  updateFooter: async (pageId, patch, opts = {}) => {
    return apiJson(`/api/pages/${encodeURIComponent(pageId)}/footer`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch || {}),
      signal: opts.signal,
    });
  },
  // ---------- AI Design Generate ----------
  aiDesign: async (pageId, payload, opts = {}) => {
    return apiJson(`/api/pages/${encodeURIComponent(pageId)}/ai-design`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload || {}),
      signal: opts.signal,
    });
  },
  updateStatus: async (id, patch, opts = {}) => {
    return apiJson(`/api/pages/${encodeURIComponent(id)}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch || {}),
      signal: opts.signal,
    });
  },

  // ---------- Upload ----------
  uploadImage: async (file, opts = {}) => {
    if (!file) throw new Error("No file provided");

    const form = new FormData();
    form.append("file", file);

    if (opts.kind) form.append("kind", String(opts.kind));
    if (opts.pageId) form.append("pageId", String(opts.pageId));
    if (opts.maxWidth) form.append("maxWidth", String(opts.maxWidth));
    if (opts.quality) form.append("quality", String(opts.quality));
    if (opts.effort != null) form.append("effort", String(opts.effort));

    const token = localStorage.getItem("sb_token");

    const uploadUrl = `${API_BASE}/api/uploads/image`;

    console.log("UPLOAD REQUEST URL:", uploadUrl);
    console.log("UPLOAD OPTIONS:", opts);
    console.log("UPLOAD FILE:", {
      name: file.name,
      type: file.type,
      size: file.size,
    });

    console.log("UPLOAD FORMDATA:");
    for (const [key, value] of form.entries()) {
      if (value instanceof File) {
        console.log(key, {
          name: value.name,
          type: value.type,
          size: value.size,
        });
      } else {
        console.log(key, value);
      }
    }

    const res = await fetch(uploadUrl, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
      signal: opts.signal,
    });

    const data = await readJson(res);

    console.log("UPLOAD RESPONSE STATUS:", res.status);
    console.log("UPLOAD RESPONSE DATA:", data);
    console.log("UPLOAD RETURNED URL:", data?.url);

    if (!res.ok) {
      const err = new Error(data?.error || "Upload failed");
      err.data = data;
      throw err;
    }

    return data.url;
  },

  // ---------- Managers (admin only) ----------
  listManagers: async (pageId, opts = {}) => {
    return apiJson(`/api/pages/${encodeURIComponent(pageId)}/users`, {
      signal: opts.signal,
    });
  },

  createManager: async (pageId, payload, opts = {}) => {
    return apiJson(`/api/pages/${encodeURIComponent(pageId)}/users`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload || {}),
      signal: opts.signal,
    });
  },

  deleteManager: async (pageId, userId, opts = {}) => {
    return apiJson(`/api/pages/${encodeURIComponent(pageId)}/users/${encodeURIComponent(userId)}`, {
      method: "DELETE",
      signal: opts.signal,
    });
  },

  updateManagerPassword: async (pageId, userId, payload, opts = {}) => {
    return apiJson(
      `/api/pages/${encodeURIComponent(pageId)}/users/${encodeURIComponent(userId)}/password`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload || {}),
        signal: opts.signal,
      }
    );
  },

  // ---------- Edit lock ----------
  acquireEditLock: async (pageId, opts = {}) => {
    return apiJson(`/api/pages/${encodeURIComponent(pageId)}/edit-lock/acquire`, {
      method: "POST",
      signal: opts.signal,
    });
  },

  heartbeatEditLock: async (pageId, opts = {}) => {
    return apiJson(`/api/pages/${encodeURIComponent(pageId)}/edit-lock/heartbeat`, {
      method: "POST",
      signal: opts.signal,
    });
  },

  releaseEditLock: async (pageId, opts = {}) => {
    return apiJson(`/api/pages/${encodeURIComponent(pageId)}/edit-lock/release`, {
      method: "POST",
      signal: opts.signal,
    });
  },
  
  // ---------- Edit lock (admin takeover) ----------
  forceEditLock: async (pageId, opts = {}) => {
    return apiJson(`/api/pages/${encodeURIComponent(pageId)}/edit-lock/force`, {
      method: "POST",
      signal: opts.signal,
    });
  },
};